import {calculateAverage} from "../../util/math-util";
import {ObjectCostRateModel} from "../object-cost-rate-model";
import {Exclude, Expose} from "class-transformer";
import {DatabaseCode, toDatabaseCode} from "../database-code";
import {FlowMeterType} from "../meter";
import {transportMethod, TransportMethod} from "../transport-method";
import {TransportCategory} from "../transport-category";

type StatisticsUnitType = "currency" | "time" | "volume"

export interface StatisticsType extends DatabaseCode {
  method?: TransportMethod
  cost?: boolean
  unitType?: StatisticsUnitType
}

const cost: StatisticsType = {
  code: "COST",
  displayValue: {
    id: "common.entity.statistics.type.cost",
    defaultMessage: "Cost"
  },
  cost: true,
  unitType: "currency",
}

const billableTime: StatisticsType = {
  code: "BILLABLE_TIME",
  displayValue: {
    id: "common.entity.statistics.type.billabletime",
    defaultMessage: "Billable Time"
  },
  cost: true,
  unitType: "time",
}

const billableQuantity: StatisticsType = {
  code: "BILLABLE_QUANTITY",
  displayValue: {
    id: "common.entity.statistics.type.billablequantity",
    defaultMessage: "Billable Quantity"
  },
  cost: true,
  unitType: "volume",
}

const timeCost: StatisticsType = {
  code: "TIME_COST",
  displayValue: {
    id: "common.entity.statistics.type.timecost",
    defaultMessage: "Time Cost"
  },
  cost: true,
  unitType: "currency",
}

const genericTimeCost: StatisticsType = {
  code: "GENERIC_TIME_COST",
  displayValue: {
    id: "common.entity.statistics.type.generictimecost",
    defaultMessage: "Generic Time Cost"
  },
  cost: true,
  unitType: "currency",
}

const siteRestrictionTimeCost: StatisticsType = {
  code: "SITE_RESTRICTION_TIME_COST",
  displayValue: {
    id: "common.entity.statistics.type.siterestrictiontimecost",
    defaultMessage: "Site Restriction Time Cost"
  },
  cost: true,
  unitType: "currency",
}

const demurrageTimeCost: StatisticsType = {
  code: "DEMURRAGE_TIME_COST",
  displayValue: {
    id: "common.entity.statistics.type.demurragetimecost",
    defaultMessage: "Demurrage Cost"
  },
  cost: true,
  unitType: "currency",
}

const pickupDemurrageTimeCost: StatisticsType = {
  code: "PICKUP_DEMURRAGE_TIME_COST",
  displayValue: {
    id: "common.entity.statistics.type.pickupdemurragetimecost",
    defaultMessage: "Pickup Demurrage Cost"
  },
  cost: true,
  unitType: "currency",
}

const deliveryDemurrageTimeCost: StatisticsType = {
  code: "DELIVERY_DEMURRAGE_TIME_COST",
  displayValue: {
    id: "common.entity.statistics.type.deliverydemurragetimecost",
    defaultMessage: "Delivery Demurrage Cost"
  },
  cost: true,
  unitType: "currency",
}

const freightCost: StatisticsType = {
  code: "FREIGHT_COST",
  displayValue: {
    id: "common.entity.statistics.type.freightcost",
    defaultMessage: "Freight Cost"
  },
  cost: true,
  unitType: "currency",
}

const genericFreightCost: StatisticsType = {
  code: "GENERIC_FREIGHT_COST",
  displayValue: {
    id: "common.entity.statistics.type.genericfreightcost",
    defaultMessage: "Generic Freight Cost"
  },
  cost: true,
  unitType: "currency",
}

const siteRestrictionFreightCost: StatisticsType = {
  code: "SITE_RESTRICTION_FREIGHT_COST",
  displayValue: {
    id: "common.entity.statistics.type.siterestrictionfreightcost",
    defaultMessage: "Site Restriction Freight Cost"
  },
  cost: true,
  unitType: "currency",
}

const loadCost: StatisticsType = {
  code: "LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.loadcost",
    defaultMessage: "Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const genericLoadCost: StatisticsType = {
  code: "GENERIC_LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.genericloadcost",
    defaultMessage: "Generic Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const siteRestrictionLoadCost: StatisticsType = {
  code: "SITE_RESTRICTION_LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.siterestrictionloadcost",
    defaultMessage: "Site Restriction Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const baseFuelSurchargeLoadCost: StatisticsType = {
  code: "BASE_FUEL_SURCHARGE_LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.basefuelsurchargeloadcost",
    defaultMessage: "Base Fuel Surcharge Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const percentFuelSurchargeLoadCost: StatisticsType = {
  code: "PERCENT_FUEL_SURCHARGE_LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.percentfuelsurchargeloadcost",
    defaultMessage: "Percent Fuel Surcharge Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const shiftLoadCost: StatisticsType = {
  code: "SHIFT_LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.shiftloadcost",
    defaultMessage: "Shift Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const ticketRejectLoadCost: StatisticsType = {
  code: "TICKET_REJECT_LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.ticketrejectloadcost",
    defaultMessage: "Reject Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const splitLoadCost: StatisticsType = {
  code: "SPLIT_LOAD_COST",
  displayValue: {
    id: "common.entity.statistics.type.splitloadcost",
    defaultMessage: "Split Load Cost"
  },
  cost: true,
  unitType: "currency",
}

const taxCost: StatisticsType = {
  code: "TAX_COST",
  displayValue: {
    id: "common.entity.statistics.type.taxcost",
    defaultMessage: "Tax Cost"
  },
  cost: true,
  unitType: "currency",
}

const siteRestrictionCost: StatisticsType = {
  code: "SITE_RESTRICTION_COST",
  displayValue: {
    id: "common.entity.statistics.type.siterestrictioncost",
    defaultMessage: "Site Restriction Cost"
  },
  cost: true,
  unitType: "currency",
}

const fuelSurchargeCost: StatisticsType = {
  code: "FUEL_SURCHARGE_COST",
  displayValue: {
    id: "common.entity.statistics.type.fuelsurchargecost",
    defaultMessage: "Fuel Surcharge Cost"
  },
  cost: true,
  unitType: "currency",
}

const quantity: StatisticsType = {
  code: "QUANTITY",
  displayValue: {
    id: "common.entity.statistics.type.quantity",
    defaultMessage: "Quantity"
  },
  unitType: "volume",
}

const standardQuantity: StatisticsType = {
  code: "STANDARD_QUANTITY",
  displayValue: {
    id: "common.entity.statistics.type.standardquantity",
    defaultMessage: "Standard Quantity"
  },
  unitType: "volume",
}

const netQuantity: StatisticsType = {
  code: "NET_QUANTITY",
  displayValue: {
    id: "common.entity.statistics.type.netquantity",
    defaultMessage: "Net Quantity"
  },
  unitType: "volume",
}

const avgDailyQuantity: StatisticsType = {
  code: "AVG_DAILY_QUANTITY",
  displayValue: {
    id: "common.entity.statistics.type.avgdailyquantity",
    defaultMessage: "Avg. Daily Quantity"
  },
  unitType: "volume",
}

const costPerQuantity: StatisticsType = {
  code: "COST_PER_QUANTITY",
  displayValue: {
    id: "common.entity.statistics.type.costperquantity",
    defaultMessage: "Cost/Quantity"
  },
  cost: true,
  unitType: "currency",
}

const loads: StatisticsType = {
  code: "LOADS",
  displayValue: {
    id: "common.entity.statistics.type.loads",
    defaultMessage: "Loads"
  },
  method: transportMethod.trucking,
}

const rejects: StatisticsType = {
  code: "REJECTS",
  displayValue: {
    id: "common.entity.statistics.type.rejects",
    defaultMessage: "Rejects"
  },
  method: transportMethod.trucking,
}

const onSiteTime: StatisticsType = {
  code: "ON_SITE_TIME",
  displayValue: {
    id: "common.entity.statistics.type.onsitetime",
    defaultMessage: "On-Site Time"
  },
  method: transportMethod.trucking,
  unitType: "time",
}

const avgOnSiteTime: StatisticsType = {
  code: "AVG_ON_SITE_TIME",
  displayValue: {
    id: "common.entity.statistics.type.avgonsitetime",
    defaultMessage: "Avg. On-Site Time"
  },
  method: transportMethod.trucking,
  unitType: "time",
}

const avgDailyOnSiteTime: StatisticsType = {
  code: "AVG_DAILY_ON_SITE_TIME",
  displayValue: {
    id: "common.entity.statistics.type.avgdailyonsitetime",
    defaultMessage: "Avg. Daily On-Site Time"
  },
  method: transportMethod.trucking,
  unitType: "time",
}

const travelTime: StatisticsType = {
  code: "TRAVEL_TIME",
  displayValue: {
    id: "common.entity.statistics.type.traveltime",
    defaultMessage: "Travel Time"
  },
  method: transportMethod.trucking,
  unitType: "time",
}

const avgTravelTime: StatisticsType = {
  code: "AVG_TRAVEL_TIME",
  displayValue: {
    id: "common.entity.statistics.type.avgtraveltime",
    defaultMessage: "Avg. Daily On-Site Time"
  },
  method: transportMethod.trucking,
  unitType: "time",
}

const turnaroundTime: StatisticsType = {
  code: "TURNAROUND_TIME",
  displayValue: {
    id: "common.entity.statistics.type.turnaroundtime",
    defaultMessage: "Turnaround Time"
  },
  method: transportMethod.trucking,
  unitType: "time",
}

const avgTurnaroundTime: StatisticsType = {
  code: "AVG_TURNAROUND_TIME",
  displayValue: {
    id: "common.entity.statistics.type.avgturnaroundtime",
    defaultMessage: "Avg. Turnaround Time"
  },
  method: transportMethod.trucking,
  unitType: "time",
}

const deliveredToNonPrimarySiteCount: StatisticsType = {
  code: "DELIVERED_TO_NON_PRIMARY_SITE_COUNT",
  displayValue: {
    id: "common.entity.statistics.type.deliveredtononprimarysitecount",
    defaultMessage: "Delivered To Non-Primary Site Count"
  },
  method: transportMethod.trucking,
}

export const statisticsType = {
  cost,
  billableTime,
  billableQuantity,
  timeCost,
  genericTimeCost,
  siteRestrictionTimeCost,
  demurrageTimeCost,
  pickupDemurrageTimeCost,
  deliveryDemurrageTimeCost,
  freightCost,
  genericFreightCost,
  siteRestrictionFreightCost,
  loadCost,
  genericLoadCost,
  siteRestrictionLoadCost,
  baseFuelSurchargeLoadCost,
  percentFuelSurchargeLoadCost,
  shiftLoadCost,
  ticketRejectLoadCost,
  splitLoadCost,
  taxCost,
  siteRestrictionCost,
  fuelSurchargeCost,
  quantity,
  standardQuantity,
  netQuantity,
  avgDailyQuantity,
  costPerQuantity,
  loads,
  rejects,
  onSiteTime,
  avgOnSiteTime,
  avgDailyOnSiteTime,
  travelTime,
  avgTravelTime,
  turnaroundTime,
  avgTurnaroundTime,
  deliveredToNonPrimarySiteCount,
}

export const statisticsTypes = Object.values(statisticsType);
export const costStatisticsTypes = statisticsTypes.filter(t => t.unitType === "currency")
export const quantityStatisticsTypes = statisticsTypes.filter(t => t.unitType === 'volume')

export const toStatisticsType = (code: string): FlowMeterType => toDatabaseCode(code, statisticsTypes)

export const supportsMethod = (method: TransportMethod | undefined, type: StatisticsType): boolean => {
  return !method || !type.method || type.method === method
}

const STATISTICS_TIME_TYPES = ['HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY'] as const;

export type StatisticsTimeType = typeof STATISTICS_TIME_TYPES[number];

export interface CostStatistics {
  billableHours: number,
  billableQty: number,
  timeCost: number,
  genericTimeCost: number,
  siteRestrictionTimeCost: number,
  pickupDemurrageTimeCost: number,
  deliveryDemurrageTimeCost: number,
  freightCost: number,
  genericFreightCost: number,
  siteRestrictionFreightCost: number,
  loadCost: number,
  genericLoadCost: number,
  siteRestrictionLoadCost: number,
  baseFuelSurchargeLoadCost: number,
  percentFuelSurchargeLoadCost: number,
  shiftLoadCost: number,
  ticketRejectLoadCost: number,
  splitLoadCost: number,
  taxCost: number,
  totalCost: number
  rateModel: string
}

@Exclude()
export class CostStatisticsWrapper implements CostStatistics {
  constructor(rateModel: string) {
    this.rateModel = rateModel;
  }

  @Expose()
  billableHours = 0

  @Expose()
  billableQty = 0;

  @Expose()
  timeCost = 0;

  @Expose()
  genericTimeCost = 0;

  @Expose()
  pickupDemurrageTimeCost = 0

  @Expose()
  deliveryDemurrageTimeCost = 0

  @Expose()
  siteRestrictionTimeCost = 0;

  @Expose()
  freightCost = 0;

  @Expose()
  genericFreightCost = 0;

  @Expose()
  siteRestrictionFreightCost = 0;

  @Expose()
  loadCost = 0;

  @Expose()
  genericLoadCost = 0;

  @Expose()
  siteRestrictionLoadCost = 0;

  @Expose()
  baseFuelSurchargeLoadCost = 0;

  @Expose()
  percentFuelSurchargeLoadCost = 0;

  @Expose()
  shiftLoadCost = 0;

  @Expose()
  ticketRejectLoadCost = 0;

  @Expose()
  splitLoadCost = 0

  @Expose()
  taxCost = 0;

  @Expose()
  totalCost = 0;

  @Expose()
  rateModel: string;

  getCostPerQty = (totalQty: number): number => {
    return calculateAverage(this.totalCost, totalQty);
  };

  get demurrageTimeCost(): number {
    return this.pickupDemurrageTimeCost + this.deliveryDemurrageTimeCost
  }

  get siteRestrictionCost(): number {
    return this.siteRestrictionTimeCost + this.siteRestrictionFreightCost + this.siteRestrictionLoadCost
  }

  get fuelSurchargeCost(): number {
    return this.baseFuelSurchargeLoadCost + this.percentFuelSurchargeLoadCost
  }
}

export interface Statistics {
  uom: string
  recordCount: number
  totalQty: number
  totalStandardQty: number
  totalNetQty: number
  lastActivityDttm?: number
  costs: Record<string, CostStatistics>
}

@Exclude()
export class StatisticsWrapper implements Statistics {
  constructor() {
    if (this.constructor === StatisticsWrapper) {
      throw new Error("Can't instantiate abstract class");
    }
  }

  @Expose()
  uom = 'barrels';

  @Expose()
  recordCount = 0;

  @Expose()
  totalQty = 0;

  @Expose()
  totalStandardQty = 0;

  @Expose()
  totalNetQty = 0;

  @Expose()
  lastActivityDttm?: number

  @Expose()
  costs: Record<string, CostStatisticsWrapper> = {}

  getValue(type: StatisticsType, rateModel: string | undefined = undefined): number {
    return 0;
  }

  addOcrmCost = (ocrm: ObjectCostRateModel) => {
    const rateModel = ocrm.rateModelName;
    if (rateModel) {
      let costStats = this.costs[rateModel];
      if (!costStats) {
        costStats = new CostStatisticsWrapper(rateModel);
        this.costs[rateModel] = costStats;
      }

      if (ocrm.billableHours) {
        costStats.billableHours += ocrm.billableHours
      }

      if (ocrm.billableQty) {
        costStats.billableQty += ocrm.billableQty
      }

      if (ocrm.timeCost) {
        costStats.timeCost += ocrm.timeCost
      }

      if (ocrm.genericTimeCost) {
        costStats.genericTimeCost += ocrm.genericTimeCost
      }

      if (ocrm.siteRestrictionTimeCost) {
        costStats.siteRestrictionTimeCost += ocrm.siteRestrictionTimeCost
      }

      if (ocrm.pickupDemurrageCost) {
        costStats.pickupDemurrageTimeCost += ocrm.pickupDemurrageCost
      }

      if (ocrm.deliveryDemurrageCost) {
        costStats.deliveryDemurrageTimeCost += ocrm.deliveryDemurrageCost
      }

      if (ocrm.freightCost) {
        costStats.freightCost += ocrm.freightCost
      }

      if (ocrm.genericFreightCost) {
        costStats.genericFreightCost += ocrm.genericFreightCost
      }

      if (ocrm.siteRestrictionFreightCost) {
        costStats.siteRestrictionFreightCost += ocrm.siteRestrictionFreightCost
      }

      if (ocrm.loadCost) {
        costStats.loadCost += ocrm.loadCost
      }

      if (ocrm.genericLoadCost) {
        costStats.genericLoadCost += ocrm.genericLoadCost
      }

      if (ocrm.siteRestrictionLoadCost) {
        costStats.siteRestrictionLoadCost += ocrm.siteRestrictionLoadCost
      }

      if (ocrm.baseFuelSurchargeLoadCost) {
        costStats.baseFuelSurchargeLoadCost += ocrm.baseFuelSurchargeLoadCost
      }

      if (ocrm.percentFuelSurchargeLoadCost) {
        costStats.percentFuelSurchargeLoadCost += ocrm.percentFuelSurchargeLoadCost
      }

      if (ocrm.shiftLoadCost) {
        costStats.shiftLoadCost += ocrm.shiftLoadCost
      }

      if (ocrm.ticketRejectLoadCost) {
        costStats.ticketRejectLoadCost += ocrm.ticketRejectLoadCost
      }

      if (ocrm.splitLoadCost) {
        costStats.splitLoadCost += ocrm.splitLoadCost
      }

      if (ocrm.taxCost) {
        costStats.taxCost += ocrm.taxCost
      }

      if (ocrm.totalCost) {
        costStats.totalCost += ocrm.totalCost
      }
    }
  };

  protected getCategoryStats(category: TransportCategory): StatisticsWrapper | undefined {
    return this
  }

  protected getCategoryStatsList(category: TransportCategory | undefined = undefined): StatisticsWrapper[] {
    return [this]
  }

  protected sum<W extends StatisticsWrapper>(categoryStatsList: W[], mapper: (categoryStats: W) => number): number {
    let value = 0

    for (const categoryStats of categoryStatsList) {
      value += mapper(categoryStats)
    }

    return value
  }

  protected combine<W extends StatisticsWrapper>(categoryStatsList: W[], mapper: (categoryStats: W) => Record<string, number>): Record<string, number> {
    const record: Record<string, number> = {}

    for (const categoryStats of categoryStatsList) {
      const categoryRecord = mapper(categoryStats)

      Object.keys(categoryRecord).forEach((key) => {
        const value = categoryRecord[key]

        if (key in record) {
          record[key] = record[key] + value
        } else {
          record[key] = value
        }
      })
    }

    return record
  }

  protected getCostValue(rateModel: string, category: TransportCategory | undefined, costFunc: (costStats: CostStatisticsWrapper) => number): number {
    let costValue = 0

    const categoryStatsList: StatisticsWrapper[] = this.getCategoryStatsList(category)
    for (const categoryStats of categoryStatsList) {
      const costStats = categoryStats.costs[rateModel]
      if (costStats) {
        costValue += costFunc(costStats)
      }
    }

    return costValue
  }

  getTotalCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.totalCost)
  }

  getBillableHours(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.billableHours)
  }

  getBillableQty(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.billableQty)
  }

  getTimeCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.timeCost)
  }

  getGenericTimeCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.genericTimeCost)
  }

  getSiteRestrictionTimeCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.siteRestrictionTimeCost)
  }

  getDemurrageTimeCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.demurrageTimeCost)
  }

  getPickupDemurrageTimeCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.pickupDemurrageTimeCost)
  }

  getDeliveryDemurrageTimeCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.deliveryDemurrageTimeCost)
  }

  getFreightCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.freightCost)
  }

  getGenericFreightCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.genericFreightCost)
  }

  getSiteRestrictionFreightCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.siteRestrictionFreightCost)
  }

  getLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.loadCost)
  }

  getGenericLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.genericLoadCost)
  }

  getSiteRestrictionLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.siteRestrictionLoadCost)
  }

  getBaseFuelSurchargeLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.baseFuelSurchargeLoadCost)
  }

  getPercentFuelSurchargeLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.percentFuelSurchargeLoadCost)
  }

  getShiftLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.shiftLoadCost)
  }

  getTicketRejectLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.ticketRejectLoadCost)
  }

  getSplitLoadCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.splitLoadCost)
  }

  getTaxCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.taxCost)
  }

  getSiteRestrictionCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.siteRestrictionCost)
  }

  getFuelSurchargeCost(rateModel: string, category: TransportCategory | undefined = undefined): number {
    return this.getCostValue(rateModel, category, costStats => costStats.fuelSurchargeCost)
  }

  getLastActivityDttm(category: TransportCategory | undefined = undefined): number | undefined {
    let dttm: number | undefined = undefined

    const categoryStatsList: StatisticsWrapper[] = this.getCategoryStatsList(category)
    for (const categoryStats of categoryStatsList) {
      if (categoryStats.lastActivityDttm) {
        if (dttm === undefined || dttm < categoryStats.lastActivityDttm) {
          dttm = categoryStats.lastActivityDttm
        }
      }
    }

    return dttm
  }

  getRateModels(category: TransportCategory | undefined = undefined): string[] {
    const rateModels = new Set<string>();

    const categoryStatsList: StatisticsWrapper[] = this.getCategoryStatsList(category)
    for (const categoryStats of categoryStatsList) {
      for (const rateModel of Object.keys(categoryStats.costs)) {
        rateModels.add(rateModel);
      }
    }

    return Array.from(rateModels)
  }
}
